import Link from 'next/link'
import { FC, useEffect, useRef, KeyboardEvent } from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { Icon } from '~/src/components/generic/PhosphorIcons/index'

import { isActivePath } from '~/src/components/SideBarWithSubmenus/helpers'

import { tw } from '~/lib/utils'

import { SidebarItem } from '~/src/navigation/types'

type Props = {
  menu: Extract<SidebarItem, { kind: 'menu' }>
  id: string
  pathname: string
  shouldAutoFocus?: boolean
  onRequestClose: () => void
  onCancelClose: () => void
  onEscape: () => void
}

const Submenu: FC<Props> = ({
  menu,
  id,
  pathname,
  shouldAutoFocus = false,
  onRequestClose,
  onCancelClose,
  onEscape,
}) => {
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([])

  const flags = useFlags()

  useEffect(() => {
    if (shouldAutoFocus) {
      itemRefs.current[0]?.focus()
    }
  }, [shouldAutoFocus])

  const handleKeyDown = (event: KeyboardEvent, index: number) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onEscape()

      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      itemRefs.current[(index + 1) % menu.items.length]?.focus()

      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      itemRefs.current[(index - 1 + menu.items.length) % menu.items.length]?.focus()

      return
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      onEscape()
    }
  }

  return (
    <div
      aria-label={`${menu.title} submenu`}
      className={tw(
        'absolute left-full top-0 z-[999] flex h-full w-48 flex-col rounded-e-2xl border-l-2 border-r border-gray-400 bg-gray-700 shadow-[0_8px_30px_rgba(0,0,0,0.18)]',
        'animate-[submenuIn_160ms_cubic-bezier(0.16,1,0.3,1)]'
      )}
      id={id}
      onMouseEnter={onCancelClose}
      onMouseLeave={onRequestClose}
    >
      <ul className="overflow-y-aut flex flex-1 translate-y-1/4 flex-col">
        {menu.items.map((item, index) => {
          const active = isActivePath(pathname, item.href)

          const isItemVisible = item.featureFlag
            ? Boolean(flags[item.featureFlag])
            : true

          if (!isItemVisible) {
            return null
          }

          return (
            <li key={item.key}>
              <Link
                aria-current={active ? 'page' : undefined}
                className={tw(
                  'flex items-center justify-between px-3 py-3 text-body-m hover:bg-ryder-red-primary/70 focus:bg-ryder-red-primary/70 focus:outline-none',
                  active && 'border-l-4 border-ryder-red-secondary bg-gray-700'
                )}
                href={item.href}
                ref={(element) => {
                  itemRefs.current[index] = element
                }}
                onClick={onRequestClose}
                onKeyDown={(event) => handleKeyDown(event, index)}
              >
                <div className="flex items-center space-x-2">
                  <Icon color="#fff" name={item.icon} size="28" />
                  <div className="space-y-1">
                    <p className="text-label-m text-inverted">{item.title}</p>
                    {item.subTitle && (
                      <p className="text-label-xs text-inverted">{item.subTitle}</p>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

Submenu.displayName = 'Submenu'

export default Submenu
